"use client";
import React, { useState, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import Image from "next/image";
import sanitizeHtml from "sanitize-html";
import ProjectModal from "../portfolio/ProjectModal";

const PortfolioCard = ({ p, index = 0 }) => {
  const [hovered, setHovered] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const cardRef = useRef(null);

  /* 3-D tilt */
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const rotateX = useSpring(useTransform(rawY, [-0.5, 0.5], [6, -6]), {
    stiffness: 120,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(rawX, [-0.5, 0.5], [-6, 6]), {
    stiffness: 120,
    damping: 20,
  });

  const handleMouseMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    rawX.set((e.clientX - rect.left) / rect.width - 0.5);
    rawY.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const reset = () => {
    rawX.set(0);
    rawY.set(0);
    setHovered(false);
  };

  const isEven = index % 2 === 0;
  const year = p?.completion_date
    ? new Date(p.completion_date).getFullYear()
    : p?.createdAt
      ? new Date(p.createdAt).getFullYear()
      : "—";

  const cleanDesc = p?.short_description
    ? sanitizeHtml(p.short_description, {
        allowedTags: ["p"],
        allowedAttributes: {},
      })
    : "";

  return (
    <>
      <motion.article
        ref={cardRef}
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
          perspective: 900,
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={reset}
        whileHover={{ y: -6 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={() => setModalOpen(true)}
        className="relative group flex flex-col overflow-hidden rounded-2xl cursor-pointer
          border border-white/[0.06] bg-white/[0.025]
          hover:border-[#00f0ff]/25 hover:bg-[#00f0ff]/[0.02]
          transition-colors duration-400"
      >
        {/* Directional glow */}
        <div
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-0"
          style={{
            background: isEven
              ? "linear-gradient(135deg, rgba(0,71,255,0.07) 0%, transparent 55%)"
              : "linear-gradient(225deg, rgba(0,240,255,0.07) 0%, transparent 55%)",
          }}
        />

        {/* Corner brackets */}
        <svg
          className="absolute top-3 left-3 z-10 opacity-20 group-hover:opacity-50 transition-opacity duration-300"
          width="14"
          height="14"
          fill="none"
        >
          <path d="M0 14 L0 0 L14 0" stroke="#00f0ff" strokeWidth="1.2" />
        </svg>
        <svg
          className="absolute bottom-3 right-3 z-10 opacity-20 group-hover:opacity-50 transition-opacity duration-300"
          width="14"
          height="14"
          fill="none"
        >
          <path d="M14 0 L14 14 L0 14" stroke="#0047ff" strokeWidth="1.2" />
        </svg>

        {/* IMAGE */}
        <div className="relative aspect-[16/9] overflow-hidden flex-shrink-0 bg-white/[0.03]">
          {p?.thumbnail ? (
            <>
              <Image
                src={p.thumbnail}
                alt={p?.projects_name || "Project"}
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              />
              <div
                className="absolute inset-0 pointer-events-none opacity-30"
                style={{
                  background:
                    "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.08) 3px, rgba(0,0,0,0.08) 4px)",
                }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(to top, rgba(5,5,11,0.85) 0%, transparent 55%)",
                }}
              />
            </>
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #0047ff10, #00f0ff05)",
              }}
            >
              <span className="text-white/10 text-4xl">◫</span>
            </div>
          )}

          {p?.categoryId?.category_name && (
            <div className="absolute top-4 left-4 z-10">
              <span
                className="px-3 py-1 rounded-full text-[9px] tracking-[0.22em] uppercase border border-[#00f0ff]/20 bg-[#05050b]/70 backdrop-blur-sm text-[#00f0ff]/70"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                {p.categoryId.category_name}
              </span>
            </div>
          )}

          {/* "View Details" hover pill */}
          <motion.div
            className="absolute inset-0 z-10 flex items-center justify-center"
            animate={{ opacity: hovered ? 1 : 0 }}
            transition={{ duration: 0.25 }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={
                hovered ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }
              }
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 bg-[#05050b]/80 backdrop-blur-md"
            >
              <span
                className="text-[10px] tracking-[0.18em] uppercase text-white/70"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                View Details
              </span>
              <span className="text-[#00f0ff] text-sm">↗</span>
            </motion.div>
          </motion.div>

          <div
            className="absolute bottom-3 right-4 z-10 text-[10px] tracking-[0.2em] text-white/30"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            {year}
          </div>
        </div>

        {/* CONTENT */}
        <div className="relative z-10 flex flex-col flex-1 p-5 md:p-6">
          <h3
            className="text-lg md:text-xl font-black text-white leading-tight mb-2"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            {p?.projects_name || "Untitled Project"}
          </h3>
          <motion.div
            className="h-px mb-3"
            animate={
              hovered
                ? { width: "100%", opacity: 1 }
                : { width: 40, opacity: 0.5 }
            }
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            style={{
              background:
                "linear-gradient(90deg, #0047ff, #00f0ff60, transparent)",
            }}
          />
          {cleanDesc && (
            <div
              className="text-sm text-white/40 leading-relaxed mb-4 line-clamp-2"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
              dangerouslySetInnerHTML={{ __html: cleanDesc }}
            />
          )}
          {p?.skills?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {p.skills.slice(0, 3).map((tech, i) => (
                <span
                  key={i}
                  className="px-2.5 py-0.5 rounded-full text-[9px] tracking-[0.14em] uppercase border border-white/[0.06] bg-white/[0.025] text-white/35"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  {tech}
                </span>
              ))}
              {p.skills.length > 3 && (
                <span
                  className="px-2.5 py-0.5 text-[9px] text-white/20"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  +{p.skills.length - 3}
                </span>
              )}
            </div>
          )}
          <div className="mt-auto pt-4 flex items-center justify-between border-t border-white/[0.05]">
            <span
              className="text-[9px] tracking-[0.22em] uppercase text-white/20"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              {String(index + 1).padStart(2, "0")}
            </span>
            <span
              className="flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-[#00f0ff]/45 group-hover:text-[#00f0ff] transition-colors duration-300"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              View Project ↗
            </span>
          </div>
        </div>
      </motion.article>

      {modalOpen && (
        <ProjectModal project={p} onClose={() => setModalOpen(false)} />
      )}
    </>
  );
};

export default PortfolioCard;
